import React, { useState, useEffect } from 'react';
import { BillingService, UsageSummary, CreditLedgerEntry } from '../../services/billingService';
import toast from 'react-hot-toast';

interface UsageByAction {
    [key: string]: {
        count: number;
        totalCredits: number;
        avgCredits: number;
    };
}

interface MonthlyUsage {
    month: string;
    purchased: number;
    consumed: number;
    expired: number;
    transactionCount: number;
}

interface CampaignUsage {
    id: number;
    name: string;
    status: string;
    creditsConsumed: number;
    creditsEstimated: number;
    tasksCount: number;
    imagesCount: number;
    createdAt: string;
}

const UserCreditsDashboard: React.FC = () => {
    const [summary, setSummary] = useState<UsageSummary | null>(null);
    const [usageByAction, setUsageByAction] = useState<UsageByAction>({});
    const [usageByCampaign, setUsageByCampaign] = useState<CampaignUsage[]>([]);
    const [monthlyUsage, setMonthlyUsage] = useState<MonthlyUsage[]>([]);
    const [ledger, setLedger] = useState<CreditLedgerEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'ledger'>('overview');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [summaryData, actionData, campaignData, monthlyData, ledgerData] = await Promise.all([
                BillingService.getUsageSummary(),
                BillingService.getUsageByAction(),
                BillingService.getUsageByCampaign(5),
                BillingService.getMonthlyUsage(6),
                BillingService.getCreditLedger(),
            ]);
            setSummary(summaryData);
            setUsageByAction(actionData);
            setUsageByCampaign(campaignData);
            setMonthlyUsage(monthlyData);
            setLedger(ledgerData);
        } catch (error) {
            console.error('Error loading credit data:', error);
            toast.error('Erro ao carregar dados de créditos');
        } finally {
            setIsLoading(false);
        }
    };

    const formatNumber = (num: number) => {
        return new Intl.NumberFormat('pt-BR').format(num);
    };

    const formatCredits = (num: number) => {
        return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(num);
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR');
    };

    const getActionLabel = (action: string) => {
        // Normalize action key (handle both camelCase and snake_case)
        const normalizedAction = action.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
        
        const labels: Record<string, string> = {
            // snake_case versions
            image_generation: 'Geração de Imagem',
            video_generation: 'Geração de Vídeo',
            brandbook_analysis: 'Análise de Brandbook',
            image_edit: 'Edição de Imagem',
            text_generation: 'Geração de Texto',
            image_upscale: 'Upscale de Imagem',
            background_removal: 'Remoção de Fundo',
            style_transfer: 'Transferência de Estilo',
            // camelCase versions (normalized)
            imagegeneration: 'Geração de Imagem',
            videogeneration: 'Geração de Vídeo',
            brandbookanalysis: 'Análise de Brandbook',
            imageedit: 'Edição de Imagem',
            textgeneration: 'Geração de Texto',
            imageupscale: 'Upscale de Imagem',
            backgroundremoval: 'Remoção de Fundo',
            styletransfer: 'Transferência de Estilo',
            other: 'Outros',
        };
        
        // Try normalized, then original, then capitalize
        return labels[normalizedAction] || labels[action.toLowerCase()] || 
            action.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
    };

    const getActionIcon = (action: string) => {
        // Normalize to snake_case for matching
        const normalizedAction = action.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
        
        const imageIcon = (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        );
        const videoIcon = (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
        );
        const docIcon = (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        );
        const editIcon = (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
        );
        const defaultIcon = (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
        );

        if (normalizedAction.includes('image') && normalizedAction.includes('generation')) return imageIcon;
        if (normalizedAction.includes('video')) return videoIcon;
        if (normalizedAction.includes('brandbook') || normalizedAction.includes('text')) return docIcon;
        if (normalizedAction.includes('edit') || normalizedAction.includes('upscale')) return editIcon;
        
        return defaultIcon;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <svg className="animate-spin h-8 w-8 text-[#7063f7]" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        );
    }

    if (!summary) {
        return (
            <div className="text-center text-snapfy-text-secondary py-12">
                Não foi possível carregar os dados de créditos.
            </div>
        );
    }

    // Calculate usage percentage
    const totalUsed = summary.totalConsumed;
    const totalAvailable = summary.currentBalance + totalUsed;
    const usagePercentage = totalAvailable > 0 ? (totalUsed / totalAvailable) * 100 : 0;
    const circumference = 2 * Math.PI * 45;

    // Find expiring credits
    const expiringCredits = ledger.filter(l => l.isExpiringSoon && !l.isExpired);
    const hasExpiringCredits = expiringCredits.length > 0;
    const totalExpiring = expiringCredits.reduce((sum, l) => sum + l.creditsRemaining, 0);

    return (
        <div className="space-y-6">
            {/* Expiration Warning */}
            {hasExpiringCredits && (
                <div className="p-4 bg-yellow-900/20 border border-yellow-500 rounded-xl flex items-center gap-3">
                    <svg className="w-6 h-6 text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                        <p className="text-yellow-400 font-medium">
                            Atenção: {formatCredits(totalExpiring)} créditos expiram em breve!
                        </p>
                        <p className="text-yellow-400/70 text-sm">
                            Use seus créditos antes que expirem para não perdê-los.
                        </p>
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-snapfy-surface rounded-xl p-5 border border-snapfy-border">
                    <p className="text-sm text-snapfy-text-secondary">Saldo Atual</p>
                    <p className="text-2xl font-bold text-green-400">{formatCredits(summary.currentBalance)}</p>
                    <p className="text-xs text-snapfy-text-secondary">créditos disponíveis</p>
                </div>
                <div className="bg-snapfy-surface rounded-xl p-5 border border-snapfy-border">
                    <p className="text-sm text-snapfy-text-secondary">Total Consumido</p>
                    <p className="text-2xl font-bold text-snapfy-accent-soft">{formatCredits(summary.totalConsumed)}</p>
                    <p className="text-xs text-snapfy-text-secondary">desde o início</p>
                </div>
                <div className="bg-snapfy-surface rounded-xl p-5 border border-snapfy-border">
                    <p className="text-sm text-snapfy-text-secondary">Este Mês</p>
                    <p className="text-2xl font-bold text-blue-400">{formatCredits(summary.currentMonthConsumed)}</p>
                    <p className="text-xs text-snapfy-text-secondary">consumidos</p>
                </div>
                <div className="bg-snapfy-surface rounded-xl p-5 border border-snapfy-border">
                    <p className="text-sm text-snapfy-text-secondary">Expirados</p>
                    <p className="text-2xl font-bold text-red-400">{formatCredits(summary.totalExpired)}</p>
                    <p className="text-xs text-snapfy-text-secondary">perdidos</p>
                </div>
            </div>

            {/* Usage Chart and Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Donut Chart */}
                <div className="bg-snapfy-surface rounded-xl p-6 border border-snapfy-border flex flex-col items-center">
                    <h3 className="text-lg font-semibold text-snapfy-text-primary mb-4">Uso de Créditos</h3>
                    <div className="relative">
                        <svg className="w-40 h-40 transform -rotate-90" viewBox="0 0 100 100">
                            <circle
                                className="text-snapfy-text-secondary"
                                strokeWidth="10"
                                stroke="currentColor"
                                fill="transparent"
                                r="45"
                                cx="50"
                                cy="50"
                            />
                            <circle
                                className="text-[#7063f7]"
                                strokeWidth="10"
                                strokeDasharray={circumference}
                                strokeDashoffset={circumference - (usagePercentage / 100) * circumference}
                                strokeLinecap="round"
                                stroke="currentColor"
                                fill="transparent"
                                r="45"
                                cx="50"
                                cy="50"
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-2xl font-bold text-snapfy-text-primary">{Math.round(usagePercentage)}%</span>
                            <span className="text-xs text-snapfy-text-secondary">utilizado</span>
                        </div>
                    </div>
                    <div className="flex gap-6 mt-4">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-[#7063f7]"></div>
                            <span className="text-sm text-snapfy-text-secondary">Usado: <span className="font-bold text-snapfy-text-primary">{formatCredits(totalUsed)}</span></span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-snapfy-inner"></div>
                            <span className="text-sm text-snapfy-text-secondary">Restante: <span className="font-bold text-snapfy-text-primary">{formatCredits(summary.currentBalance)}</span></span>
                        </div>
                    </div>
                </div>

                {/* Output Stats */}
                <div className="bg-snapfy-surface rounded-xl p-6 border border-snapfy-border">
                    <h3 className="text-lg font-semibold text-snapfy-text-primary mb-4">Resultados Gerados</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-snapfy-inner rounded-xl">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-[#7063f7]/20 rounded-lg">
                                    <svg className="w-6 h-6 text-snapfy-accent-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-snapfy-text-primary">{formatNumber(summary.imagesGenerated)}</p>
                                    <p className="text-sm text-snapfy-text-secondary">Imagens Geradas</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-snapfy-text-secondary">Custo médio</p>
                                <p className="text-lg font-semibold text-snapfy-accent-soft">{formatCredits(summary.avgCostPerImage)}</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-snapfy-inner rounded-xl">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-500/20 rounded-lg">
                                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-snapfy-text-primary">{formatNumber(summary.videosGenerated)}</p>
                                    <p className="text-sm text-snapfy-text-secondary">Vídeos Gerados</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-snapfy-text-secondary">Custo médio</p>
                                <p className="text-lg font-semibold text-blue-400">{formatCredits(summary.avgCostPerVideo)}</p>
                            </div>
                        </div>
                        <div className="flex items-center justify-between p-4 bg-snapfy-inner rounded-xl">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-500/20 rounded-lg">
                                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-snapfy-text-primary">{formatNumber(summary.campaignsCount)}</p>
                                    <p className="text-sm text-snapfy-text-secondary">Campanhas Criadas</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-snapfy-border">
                <nav className="flex gap-4">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'overview'
                                ? 'border-[#7063f7] text-snapfy-accent-soft'
                                : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                        }`}
                    >
                        Consumo por Tipo
                    </button>
                    <button
                        onClick={() => setActiveTab('campaigns')}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'campaigns'
                                ? 'border-[#7063f7] text-snapfy-accent-soft'
                                : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                        }`}
                    >
                        Por Campanha
                    </button>
                    <button
                        onClick={() => setActiveTab('ledger')}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === 'ledger'
                                ? 'border-[#7063f7] text-snapfy-accent-soft'
                                : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                        }`}
                    >
                        Pacotes de Créditos
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-snapfy-surface rounded-xl p-6 border border-snapfy-border">
                {activeTab === 'overview' && (
                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-snapfy-text-primary mb-4">Consumo por Tipo de Ação</h3>
                        {Object.entries(usageByAction).length > 0 ? (
                            Object.entries(usageByAction).map(([action, data]) => (
                                <div key={action} className="flex items-center justify-between p-4 bg-snapfy-inner rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${
                                            action === 'image_generation' ? 'bg-[#7063f7]/20 text-snapfy-accent-soft' :
                                            action === 'video_generation' ? 'bg-blue-500/20 text-blue-400' :
                                            action === 'brandbook_analysis' ? 'bg-green-500/20 text-green-400' :
                                            action === 'image_edit' ? 'bg-yellow-500/20 text-yellow-400' :
                                            'bg-snapfy-inner text-snapfy-text-secondary'
                                        }`}>
                                            {getActionIcon(action)}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-snapfy-text-primary">{getActionLabel(action)}</p>
                                            <p className="text-xs text-snapfy-text-secondary">{data.count} operações • média {formatCredits(data.avgCredits)} créditos</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-semibold text-snapfy-accent-soft">{formatCredits(data.totalCredits)}</p>
                                        <p className="text-xs text-snapfy-text-secondary">créditos</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-snapfy-text-secondary py-4">Nenhum consumo registrado ainda.</p>
                        )}
                    </div>
                )}

                {activeTab === 'campaigns' && (
                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-snapfy-text-primary mb-4">Top Campanhas por Consumo</h3>
                        {usageByCampaign.length > 0 ? (
                            usageByCampaign.map((campaign) => (
                                <div key={campaign.id} className="p-4 bg-snapfy-inner rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-medium text-snapfy-text-primary">{campaign.name}</p>
                                        <span className={`px-2 py-1 text-xs rounded-full ${
                                            campaign.status === 'completed' ? 'bg-green-900/30 text-green-400' :
                                            campaign.status === 'processing' ? 'bg-blue-900/30 text-blue-400' :
                                            campaign.status === 'draft' ? 'bg-snapfy-inner text-snapfy-text-secondary' :
                                            'bg-yellow-900/30 text-yellow-400'
                                        }`}>
                                            {campaign.status}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 text-xs">
                                        <div>
                                            <p className="text-snapfy-text-secondary">Consumido</p>
                                            <p className="text-snapfy-accent-soft font-medium">{formatCredits(campaign.creditsConsumed)}</p>
                                        </div>
                                        <div>
                                            <p className="text-snapfy-text-secondary">Estimado</p>
                                            <p className="text-snapfy-text-secondary">{formatCredits(campaign.creditsEstimated)}</p>
                                        </div>
                                        <div>
                                            <p className="text-snapfy-text-secondary">Imagens</p>
                                            <p className="text-snapfy-text-secondary">{campaign.imagesCount}</p>
                                        </div>
                                        <div>
                                            <p className="text-snapfy-text-secondary">Criada</p>
                                            <p className="text-snapfy-text-secondary">{formatDate(campaign.createdAt)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-snapfy-text-secondary py-4">Nenhuma campanha encontrada.</p>
                        )}
                    </div>
                )}

                {activeTab === 'ledger' && (
                    <div className="space-y-3">
                        <h3 className="text-lg font-semibold text-snapfy-text-primary mb-4">Seus Pacotes de Créditos</h3>
                        {ledger.length > 0 ? (
                            ledger.filter(l => l.creditsRemaining > 0 || l.isExpired).map((entry) => (
                                <div key={entry.id} className={`p-4 rounded-lg border ${
                                    entry.isExpired ? 'bg-red-900/10 border-red-800' :
                                    entry.isExpiringSoon ? 'bg-yellow-900/10 border-yellow-800' :
                                    'bg-snapfy-inner border-snapfy-border'
                                }`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-snapfy-text-primary">
                                                {entry.sourceDescription || 'Pacote de Créditos'}
                                            </p>
                                            {entry.isPromotional && (
                                                <span className="px-2 py-0.5 text-xs bg-[#1a0a3e]/30 text-snapfy-accent-soft rounded-full">
                                                    Promocional
                                                </span>
                                            )}
                                            {entry.isExpired && (
                                                <span className="px-2 py-0.5 text-xs bg-red-900/30 text-red-400 rounded-full">
                                                    Expirado
                                                </span>
                                            )}
                                            {entry.isExpiringSoon && !entry.isExpired && (
                                                <span className="px-2 py-0.5 text-xs bg-yellow-900/30 text-yellow-400 rounded-full">
                                                    Expira em {entry.daysUntilExpiration} dias
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 text-xs">
                                        <div>
                                            <p className="text-snapfy-text-secondary">Concedido</p>
                                            <p className="text-snapfy-text-secondary font-medium">{formatCredits(entry.creditsGranted)}</p>
                                        </div>
                                        <div>
                                            <p className="text-snapfy-text-secondary">Usado</p>
                                            <p className="text-snapfy-accent-soft font-medium">{formatCredits(entry.creditsUsed)}</p>
                                        </div>
                                        <div>
                                            <p className="text-snapfy-text-secondary">Restante</p>
                                            <p className={`font-medium ${entry.isExpired ? 'text-red-400' : 'text-green-400'}`}>
                                                {formatCredits(entry.creditsRemaining)}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-snapfy-text-secondary">Expira em</p>
                                            <p className="text-snapfy-text-secondary">
                                                {entry.expiresAt ? formatDate(entry.expiresAt) : 'Nunca'}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="mt-3">
                                        <div className="w-full bg-snapfy-inner rounded-full h-2">
                                            <div 
                                                className={`h-2 rounded-full ${entry.isExpired ? 'bg-red-500' : 'bg-[#7063f7]'}`}
                                                style={{ width: `${(entry.creditsUsed / entry.creditsGranted) * 100}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-center text-snapfy-text-secondary py-4">Nenhum pacote de créditos encontrado.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Monthly Usage Chart */}
            {monthlyUsage.length > 0 && (
                <div className="bg-snapfy-surface rounded-xl p-6 border border-snapfy-border">
                    <h3 className="text-lg font-semibold text-snapfy-text-primary mb-4">Histórico Mensal</h3>
                    <div className="space-y-3">
                        {monthlyUsage.map((month) => (
                            <div key={month.month} className="flex items-center gap-4">
                                <div className="w-20 text-sm text-snapfy-text-secondary">{month.month}</div>
                                <div className="flex-1">
                                    <div className="flex gap-2 h-6">
                                        {month.consumed > 0 && (
                                            <div 
                                                className="bg-[#7063f7] rounded"
                                                style={{ width: `${Math.min((month.consumed / Math.max(...monthlyUsage.map(m => m.consumed || 1))) * 100, 100)}%` }}
                                                title={`Consumido: ${formatCredits(month.consumed)}`}
                                            ></div>
                                        )}
                                    </div>
                                </div>
                                <div className="w-24 text-right">
                                    <span className="text-sm text-snapfy-accent-soft font-medium">{formatCredits(month.consumed)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-4 mt-4 text-xs text-snapfy-text-secondary">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-[#7063f7]"></div>
                            <span>Consumido</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserCreditsDashboard;
